import type ExcelJSType from 'exceljs';
import type { ExcelRow } from './types';
import { XLSX_MIME_TYPE } from './constants';

export interface SerializeExcelOptions {
  sheetName?: string;
  headers?: string[];
}

const DEFAULT_SHEET_NAME = 'Sheet1';

function determineHeaders(rows: ExcelRow[], options?: SerializeExcelOptions): string[] {
  if (options?.headers) {
    return options.headers;
  }

  if (rows.length === 0) {
    return [];
  }

  return Object.keys(rows[0]);
}

function fillWorksheet(
  worksheet: ExcelJSType.Worksheet,
  rows: ExcelRow[],
  headers: string[],
): void {
  worksheet.columns = headers.map((header) => ({ header, key: header }));
  rows.forEach((row) => {
    worksheet.addRow(row);
  });
}

async function buildWorkbook(
  rows: ExcelRow[],
  options?: SerializeExcelOptions,
): Promise<ExcelJSType.Workbook> {
  const ExcelJS = (await import('exceljs')).default;
  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet(options?.sheetName ?? DEFAULT_SHEET_NAME);
  const headers = determineHeaders(rows, options);

  fillWorksheet(worksheet, rows, headers);

  return workbook;
}

export async function serializeExcel(
  rows: ExcelRow[],
  options?: SerializeExcelOptions,
): Promise<Blob> {
  const workbook = await buildWorkbook(rows, options);
  const buffer = await workbook.xlsx.writeBuffer();

  return new Blob([buffer], { type: XLSX_MIME_TYPE });
}
